import { useEffect, useState, useCallback } from "react"
import { getWallets, freezeWallet, unfreezeWallet } from "@/services/payout.service"
import { useToast } from "@/context/ToastContext"
import { Badge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { Modal, FormGroup, Textarea } from "@/components/common/Modal"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { RefreshCw, Snowflake, Flame, Search } from "lucide-react"
import type { Wallet } from "@/types"

type WalletWithUser = Wallet & { user_email: string; user_name: string | null }

export function WalletsPage() {
  const { toast } = useToast()
  const [wallets, setWallets]   = useState<WalletWithUser[]>([])
  const [loading, setLoading]   = useState(true)
  const [search, setSearch]     = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [freezeOpen, setFreezeOpen]     = useState(false)
  const [freezeTarget, setFreezeTarget] = useState<WalletWithUser | null>(null)
  const [freezeReason, setFreezeReason] = useState("")
  const [acting, setActing]     = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getWallets({ status: statusFilter === "all" ? undefined : statusFilter })
      setWallets(data)
    } catch { toast("Failed to load wallets", "error") }
    finally { setLoading(false) }
  }, [statusFilter, toast])

  useEffect(() => { load() }, [load])

  const filtered = wallets.filter(w => {
    if (!search) return true
    const q = search.toLowerCase()
    return w.user_email.toLowerCase().includes(q) ||
           (w.user_name ?? "").toLowerCase().includes(q)
  })

  const totalBalance  = wallets.reduce((s, w) => s + (w.balance ?? 0), 0)
  const totalPending  = wallets.reduce((s, w) => s + (w.pending ?? 0), 0)
  const totalLifetime = wallets.reduce((s, w) => s + (w.lifetime_earned ?? 0), 0)

  const handleFreeze = async () => {
    if (!freezeTarget || !freezeReason) { toast("Reason required", "error"); return }
    setActing(freezeTarget.id)
    try {
      await freezeWallet(freezeTarget.id, freezeReason)
      toast(`Wallet frozen for ${freezeTarget.user_email}`, "success")
      setFreezeOpen(false); setFreezeReason("")
      load()
    } catch { toast("Failed to freeze wallet", "error") }
    finally { setActing(null) }
  }

  const handleUnfreeze = async (w: WalletWithUser) => {
    setActing(w.id)
    try {
      await unfreezeWallet(w.id)
      toast(`Wallet unfrozen for ${w.user_email}`, "success")
      load()
    } catch { toast("Failed to unfreeze wallet", "error") }
    finally { setActing(null) }
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Available Balance", value: `$${totalBalance.toFixed(2)}`,  color: "bg-green-500"  },
          { label: "Total Pending",           value: `$${totalPending.toFixed(2)}`,  color: "bg-amber-500"  },
          { label: "Lifetime Earned",         value: `$${totalLifetime.toFixed(2)}`, color: "bg-zimrah-500" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className={`h-1 ${s.color}`} />
            <div className="p-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">{s.label}</p>
              <p className="text-2xl font-black text-gray-900 tabular-nums">{loading ? "—" : s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zimrah-500 transition-all" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-zimrah-500 transition-all">
          <option value="all">All Wallets</option>
          <option value="active">Active</option>
          <option value="frozen">Frozen</option>
          <option value="closed">Closed</option>
        </select>
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex justify-between items-center">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Member Wallets</h2>
          <Badge variant="muted">{filtered.length} wallets</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["Member", "Status", "Available Balance", "Pending", "Lifetime Earned", "Lifetime Paid", "Actions"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={7} /> :
               filtered.length === 0 ? (
                <tr><td colSpan={7}>
                  <EmptyState icon="👛" title="No wallets found" subtitle="Member wallets appear here once created." />
                </td></tr>
               ) : filtered.map(w => (
                <tr key={w.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-bold text-sm text-gray-900">{w.user_name ?? "—"}</p>
                    <p className="text-xs text-gray-400">{w.user_email}</p>
                  </td>
                  <td className="px-6 py-4">
                    {w.status === "active"  ? <Badge variant="success" dot>Active</Badge>  :
                     w.status === "frozen"  ? <Badge variant="danger"  dot>Frozen</Badge>  :
                     <Badge variant="muted" dot>Closed</Badge>}
                  </td>
                  <td className="px-6 py-4 font-black text-gray-900 tabular-nums">
                    ${(w.balance ?? 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 font-semibold text-amber-600 tabular-nums">
                    ${(w.pending ?? 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 font-semibold text-green-600 tabular-nums">
                    ${(w.lifetime_earned ?? 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 font-semibold text-gray-500 tabular-nums">
                    ${(w.lifetime_paid ?? 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4">
                    {w.status === "frozen" ? (
                      <Button size="sm" variant="success" icon={<Flame size={13} />}
                        loading={acting === w.id}
                        onClick={() => handleUnfreeze(w)}>
                        Unfreeze
                      </Button>
                    ) : (
                      <Button size="sm" variant="secondary" icon={<Snowflake size={13} />}
                        onClick={() => { setFreezeTarget(w); setFreezeOpen(true) }}>
                        Freeze
                      </Button>
                    )}
                  </td>
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Freeze Modal */}
      <Modal open={freezeOpen} onClose={() => setFreezeOpen(false)} title="Freeze Wallet" icon="🧊"
        footer={
          <>
            <Button variant="secondary" onClick={() => setFreezeOpen(false)}>Cancel</Button>
            <Button variant="danger" loading={acting === freezeTarget?.id} onClick={handleFreeze}>
              Freeze Wallet
            </Button>
          </>
        }
      >
        <FormGroup label="Member">
          <p className="text-sm font-bold text-gray-900">{freezeTarget?.user_name ?? "—"}</p>
          <p className="text-xs text-gray-400">{freezeTarget?.user_email}</p>
        </FormGroup>
        <FormGroup label="Current Balance">
          <p className="text-2xl font-black text-gray-900">${(freezeTarget?.balance ?? 0).toFixed(2)}</p>
        </FormGroup>
        <FormGroup label="Reason for Freezing" required>
          <Textarea value={freezeReason} onChange={e => setFreezeReason(e.target.value)}
            placeholder="Explain why this wallet is being frozen…" />
        </FormGroup>
        <div className="p-3 rounded-xl bg-red-50 border border-red-100">
          <p className="text-xs font-semibold text-red-600">
            ⚠️ Freezing prevents the member from requesting payouts. This is logged in the audit trail.
          </p>
        </div>
      </Modal>
    </div>
  )
}